import { useState } from 'react';
import type { Address, Hash, PublicClient } from 'viem';
import { useWalletClient } from 'wagmi';

import { queryClient } from 'clients/api/queryClient';
import type { TransactionQueueItem } from 'components';
import FunctionKey from 'constants/functionKey';
import { erc20Abi, vBep20Abi } from 'libs/contracts';
import { stakingV2Abi } from 'libs/contracts/abis/bittensorPrecompiles';
import { wAlphaAbi, wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';
import { STAKING_V2_ADDRESS } from 'libs/contracts/config/bittensorPrecompiles';
import { useAccountAddress, useChainId, usePublicClient } from 'libs/wallet';
import type { VToken } from 'types';

import {
  ONE_TOKEN,
  RAO_WEI,
  calculateSlippageFloor,
  computeRouterDeadline,
} from '../shared/routerSlippage';
import type { BittensorNativeSupplySource } from './getBittensorSupplySourceTokens';

// Re-exported from shared/routerSlippage to preserve the public surface that
// `__tests__/useBittensorSupplyQueue.spec.ts` reaches into. The canonical definitions
// live in `shared/routerSlippage.ts` so the legacy native-TAO supply path and the
// PositionRouter queue hooks share a single source of truth for slippage math.
export { RAO_WEI, calculateSlippageFloor };

export interface BittensorSupplyQueuePlanStep extends TransactionQueueItem {
  shouldSkip: boolean;
  minAmountOut?: bigint;
}

const getWrappedOut = ({
  amountMantissa,
  exchangeRate,
}: {
  amountMantissa: bigint;
  exchangeRate: bigint;
}) => {
  if (exchangeRate <= 0n) {
    return 0n;
  }

  return (amountMantissa * ONE_TOKEN) / exchangeRate;
};

export const buildNativeTaoSupplyQueuePlan = ({
  amountMantissa,
  erc20AllowanceMantissa,
  exchangeRate,
  slippagePercentage,
  vToken,
}: {
  amountMantissa: bigint;
  erc20AllowanceMantissa: bigint;
  exchangeRate: bigint;
  slippagePercentage: number;
  vToken: VToken;
}): [BittensorSupplyQueuePlanStep, BittensorSupplyQueuePlanStep, BittensorSupplyQueuePlanStep] => {
  if (amountMantissa % RAO_WEI !== 0n) {
    throw new Error('TAO amount must be a whole RAO increment');
  }

  const wrappedAmountOut = getWrappedOut({ amountMantissa, exchangeRate });
  const minAmountOut = calculateSlippageFloor({
    amount: wrappedAmountOut,
    slippagePercentage,
  });
  const symbol = vToken.underlyingToken.symbol;

  return [
    {
      id: 'wrap-tao',
      label: `Wrap TAO to ${symbol}`,
      minAmountOut,
      shouldSkip: false,
      status: 'idle',
    },
    {
      id: 'approve-wrapped-token',
      label: `Approve ${symbol}`,
      shouldSkip: erc20AllowanceMantissa >= wrappedAmountOut,
      status: 'idle',
    },
    {
      id: 'supply-wrapped-token',
      label: `Supply ${symbol}`,
      shouldSkip: false,
      status: 'idle',
    },
  ];
};

export const buildNativeAlphaSupplyQueuePlan = ({
  amountMantissa,
  erc20AllowanceMantissa,
  receivedWrappedAmountMantissa,
  slippagePercentage,
  source,
  vToken,
}: {
  amountMantissa: bigint;
  erc20AllowanceMantissa: bigint;
  receivedWrappedAmountMantissa: bigint;
  slippagePercentage: number;
  source: BittensorNativeSupplySource;
  vToken: VToken;
}): [
  BittensorSupplyQueuePlanStep,
  BittensorSupplyQueuePlanStep,
  BittensorSupplyQueuePlanStep,
  BittensorSupplyQueuePlanStep,
] => {
  const wrappedAmountOut = getWrappedOut({
    amountMantissa,
    exchangeRate: source.exchangeRate,
  });
  const minAmountOut = calculateSlippageFloor({
    amount: wrappedAmountOut,
    slippagePercentage,
  });
  const symbol = vToken.underlyingToken.symbol;

  return [
    {
      id: 'approve-native-alpha',
      label: 'Approve native alpha spend',
      shouldSkip: BigInt(source.stakingAllowanceMantissa.toFixed()) >= amountMantissa,
      status: 'idle',
    },
    {
      id: 'wrap-alpha',
      label: `Wrap alpha to ${symbol}`,
      minAmountOut,
      shouldSkip: false,
      status: 'idle',
    },
    {
      id: 'approve-wrapped-token',
      label: `Approve ${symbol}`,
      shouldSkip: erc20AllowanceMantissa >= receivedWrappedAmountMantissa,
      status: 'idle',
    },
    {
      id: 'supply-wrapped-token',
      label: `Supply ${symbol}`,
      shouldSkip: false,
      status: 'idle',
    },
  ];
};

interface ExecuteBittensorSupplyQueueInput {
  amountMantissa: bigint;
  nativeAlphaSource?: BittensorNativeSupplySource;
  slippagePercentage: number;
  vToken: VToken;
}

const getErc20Allowance = async ({
  accountAddress,
  publicClient,
  spenderAddress,
  tokenAddress,
}: {
  accountAddress: Address;
  publicClient: PublicClient;
  spenderAddress: Address;
  tokenAddress: Address;
}) =>
  publicClient.readContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [accountAddress, spenderAddress],
  });

const waitForHash = async ({
  hash,
  publicClient,
}: {
  hash: Hash;
  publicClient: PublicClient;
}) => {
  await publicClient.waitForTransactionReceipt({ hash });
};

const invalidateBittensorSupplyQueries = ({
  accountAddress,
  chainId,
  tokenAddress,
  vTokenAddress,
}: {
  accountAddress: Address;
  chainId: number;
  tokenAddress: Address;
  vTokenAddress: Address;
}) => {
  queryClient.invalidateQueries({ queryKey: [FunctionKey.GET_POOLS] });
  queryClient.invalidateQueries({
    queryKey: [FunctionKey.GET_TOKEN_BALANCES, { chainId, accountAddress }],
  });
  queryClient.invalidateQueries({
    queryKey: [FunctionKey.GET_BALANCE_OF, { chainId, accountAddress, tokenAddress }],
  });
  queryClient.invalidateQueries({
    queryKey: [FunctionKey.GET_V_TOKEN_BALANCE, { chainId, accountAddress, vTokenAddress }],
  });
  queryClient.invalidateQueries({
    queryKey: [FunctionKey.GET_BITTENSOR_NATIVE_BALANCES],
  });
};

export const useBittensorSupplyQueue = () => {
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { data: walletClient } = useWalletClient({ account: accountAddress });
  const [items, setItems] = useState<TransactionQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateItem = (id: string, status: TransactionQueueItem['status']) => {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const runStep = async ({
    action,
    id,
    shouldSkip,
  }: BittensorSupplyQueuePlanStep & { action: () => Promise<Hash> }) => {
    if (shouldSkip) {
      updateItem(id, 'skipped');
      return;
    }

    updateItem(id, 'pendingSignature');
    const hash = await action();
    updateItem(id, 'pendingConfirmation');
    await waitForHash({ hash, publicClient });
    updateItem(id, 'success');
  };

  const execute = async ({
    amountMantissa,
    nativeAlphaSource,
    slippagePercentage,
    vToken,
  }: ExecuteBittensorSupplyQueueInput) => {
    if (!accountAddress || !walletClient) {
      throw new Error('Wallet is not connected');
    }

    setIsRunning(true);

    try {
      if (nativeAlphaSource) {
        const erc20AllowanceMantissa = await getErc20Allowance({
          accountAddress,
          publicClient,
          spenderAddress: vToken.address,
          tokenAddress: nativeAlphaSource.wrapperAddress,
        });
        const plan = buildNativeAlphaSupplyQueuePlan({
          amountMantissa,
          erc20AllowanceMantissa,
          receivedWrappedAmountMantissa: getWrappedOut({
            amountMantissa,
            exchangeRate: nativeAlphaSource.exchangeRate,
          }),
          slippagePercentage,
          source: nativeAlphaSource,
          vToken,
        });
        setItems(plan);

        await runStep({
          ...plan[0],
          action: () =>
            walletClient.writeContract({
              address: STAKING_V2_ADDRESS,
              abi: stakingV2Abi,
              functionName: 'approve',
              args: [nativeAlphaSource.wrapperAddress, nativeAlphaSource.netuid, amountMantissa],
              account: accountAddress,
            }),
        });

        const wrapRequest = {
          address: nativeAlphaSource.wrapperAddress,
          abi: wAlphaAbi,
          functionName: 'wrap',
          args: [amountMantissa, plan[1].minAmountOut ?? 0n, computeRouterDeadline()],
          account: accountAddress,
        } as const;
        const { result: receivedWrappedAmountMantissa } =
          await publicClient.simulateContract(wrapRequest);
        await runStep({
          ...plan[1],
          action: () => walletClient.writeContract(wrapRequest),
        });

        const approvalStep = {
          ...plan[2],
          shouldSkip: erc20AllowanceMantissa >= receivedWrappedAmountMantissa,
        };
        await runStep({
          ...approvalStep,
          action: () =>
            walletClient.writeContract({
              address: nativeAlphaSource.wrapperAddress,
              abi: erc20Abi,
              functionName: 'approve',
              args: [vToken.address, receivedWrappedAmountMantissa],
              account: accountAddress,
            }),
        });

        await runStep({
          ...plan[3],
          action: () =>
            walletClient.writeContract({
              address: vToken.address,
              abi: vBep20Abi,
              functionName: 'mint',
              args: [receivedWrappedAmountMantissa],
              account: accountAddress,
            }),
        });
      } else {
        const exchangeRate = await publicClient.readContract({
          address: vToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
        const erc20AllowanceMantissa = await getErc20Allowance({
          accountAddress,
          publicClient,
          spenderAddress: vToken.address,
          tokenAddress: vToken.underlyingToken.address,
        });
        const plan = buildNativeTaoSupplyQueuePlan({
          amountMantissa,
          erc20AllowanceMantissa,
          exchangeRate,
          slippagePercentage,
          vToken,
        });
        setItems(plan);

        const wrapRequest = {
          address: vToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'wrap',
          args: [plan[0].minAmountOut ?? 0n, computeRouterDeadline()],
          value: amountMantissa,
          account: accountAddress,
        } as const;
        const { result: receivedWrappedAmountMantissa } =
          await publicClient.simulateContract(wrapRequest);
        await runStep({
          ...plan[0],
          action: () => walletClient.writeContract(wrapRequest),
        });

        const approvalStep = {
          ...plan[1],
          shouldSkip: erc20AllowanceMantissa >= receivedWrappedAmountMantissa,
        };
        await runStep({
          ...approvalStep,
          action: () =>
            walletClient.writeContract({
              address: vToken.underlyingToken.address,
              abi: erc20Abi,
              functionName: 'approve',
              args: [vToken.address, receivedWrappedAmountMantissa],
              account: accountAddress,
            }),
        });

        await runStep({
          ...plan[2],
          action: () =>
            walletClient.writeContract({
              address: vToken.address,
              abi: vBep20Abi,
              functionName: 'mint',
              args: [receivedWrappedAmountMantissa],
              account: accountAddress,
            }),
        });
      }

      invalidateBittensorSupplyQueries({
        accountAddress,
        chainId,
        tokenAddress: vToken.underlyingToken.address,
        vTokenAddress: vToken.address,
      });
    } catch (error) {
      setItems((currentItems) => {
        let failedActiveStep = false;

        return currentItems.map((item) => {
          if (item.status === 'success' || item.status === 'skipped' || item.status === 'failed') {
            return item;
          }

          if (item.status === 'pendingSignature' || item.status === 'pendingConfirmation') {
            failedActiveStep = true;
            return { ...item, status: 'failed' };
          }

          return failedActiveStep || item.status === 'idle' ? { ...item, status: 'failed' } : item;
        });
      });
      throw error;
    } finally {
      setIsRunning(false);
    }
  };

  return { execute, isRunning, items };
};
